import { formatReceiptCurrency } from '../utils/formatCurrency';
import { translations } from '../constants/translations';
import {
  getSavedCounterIp,
  getSavedCounterUsb,
  getSavedKitchenIp,
  getSavedKitchenUsb,
  getReceiptLanguage,
} from '../utils/posPrinterSettings';

/**
 * Build a translator bound to a specific language (mirrors LanguageContext.t),
 * with English fallback. Used to print receipts in a language independent of
 * the app/system language.
 */
const createReceiptTranslator = (lang) => (key, params = {}) => {
  if (!key || typeof key !== 'string') return key || '';
  const lookup = (dict) => {
    let value = dict;
    for (const k of key.split('.')) {
      if (value && typeof value === 'object' && value[k] !== undefined) value = value[k];
      else return undefined;
    }
    return value;
  };
  let value = lookup(translations[lang]);
  if (value === undefined) value = lookup(translations.en);
  if (typeof value === 'string' && Object.keys(params).length > 0) {
    return value.replace(/\{\{(\w+)\}\}/g, (m, p) => (params[p] !== undefined ? params[p] : m));
  }
  return typeof value === 'string' ? value : key;
};

/**
 * Resolve which translator + language a receipt should use. When a receipt
 * language is configured in POS Settings it overrides the app language (and its
 * translator); otherwise the caller's app-language t/language are kept.
 */
export const resolveReceiptLocale = (t, language) => {
  const rl = getReceiptLanguage();
  if (rl && translations[rl]) {
    return { t: createReceiptTranslator(rl), language: rl };
  }
  return { t, language };
};
const STORAGE_SERVICE_URL = 'pos_print_service_url';
const STORAGE_AUTO_PRINT_MODIFICATION = 'pos_auto_print_modification';
const THERMAL_80MM_CHARS = 42;
/** Kitchen slip: name + qty only (no prices) — 31 + 1 + 10 = 42 */
const KITCHEN_COL_NAME = 31;
const KITCHEN_COL_QTY = 10;

/**
 * Align printed receipt dates with app language, and always use the branch
 * timezone (not the OS timezone) so receipts are correct regardless of how
 * the machine's OS clock is configured.
 *
 * @param {string|null} language - UI language code ('tr', 'ur', 'de', …)
 * @param {string|null} timezone - IANA timezone, e.g. 'Asia/Karachi'. Falls
 *   back to the browser / OS local timezone when null or invalid.
 */
export const getReceiptDateTimeStrings = (language, timezone) => {
  const d = new Date();
  const loc =
    language === 'tr' ? 'tr-TR' :
    language === 'ur' ? 'ur-PK' :
    language === 'de' ? 'de-DE' :
    undefined;

  // Receipt time policy:
  //  - Desktop (Electron): ALWAYS use the POS PC's own OS timezone — the machine
  //    physically sits in the restaurant, so its local time is the source of truth
  //    for what staff/customers see on the slip. (Backend reports/analytics keep
  //    using the branch-settings timezone separately.)
  //  - Web: use the branch timezone, treating the unconfigured 'UTC' default as
  //    "not set" → fall back to the browser's local timezone.
  const isElectron = typeof window !== 'undefined' && !!window.electronAPI?.isElectron;
  const effectiveTz = isElectron
    ? undefined // device/OS local timezone
    : (timezone && String(timezone).trim().toUpperCase() !== 'UTC' ? timezone : undefined);
  const tzOpts = effectiveTz ? { timeZone: effectiveTz } : {};

  try {
    return {
      dateStr: d.toLocaleDateString(loc, tzOpts),
      timeStr: d.toLocaleTimeString(loc, tzOpts),
    };
  } catch {
    // Invalid timezone — fall back to OS local time
    return {
      dateStr: d.toLocaleDateString(loc),
      timeStr: d.toLocaleTimeString(loc),
    };
  }
};

export const isAutoPrintModificationEnabled = () => {
  try {
    const value = localStorage.getItem(STORAGE_AUTO_PRINT_MODIFICATION);
    if (value === null) return true;
    return value !== 'false';
  } catch (_) {
    return true;
  }
};

export const hasKitchenPrinterConfigured = () => {
  try {
    return !!(getSavedKitchenUsb() || getSavedKitchenIp());
  } catch (_) {
    return false;
  }
};

export const ITEM_TYPE_DINE_IN = 'dine-in';
export const ITEM_TYPE_TAKEAWAY = 'takeaway';

export const getOrderItemType = (item) =>
  item?.itemType === ITEM_TYPE_TAKEAWAY || item?.item_type === 'takeaway'
    ? ITEM_TYPE_TAKEAWAY
    : ITEM_TYPE_DINE_IN;

const normalizeOrderItemId = (item) => {
  const type = getOrderItemType(item);
  const id =
    item?.cartItemId ??
    item?.menuItemId ??
    item?.menu_item_id ??
    item?._id ??
    item?.id;
  if (id != null && String(id) !== '') return `${String(id)}|${type}`;
  const name = String(item?.name || '').trim().toLowerCase();
  return name ? `name:${name}|${type}` : '';
};

const isKitchenPrinted = (item) => !!(item?.kitchenPrinted || item?.kitchen_printed);

/** Items not yet sent to kitchen (optionally for dine-in vs takeaway bucket). */
export function getKitchenPendingItems(items = [], itemTypeFilter = null) {
  return (items || []).filter((item) => {
    if (isKitchenPrinted(item)) return false;
    if (!itemTypeFilter) return true;
    return getOrderItemType(item) === itemTypeFilter;
  });
}

export function orderHasMixedItemTypes(items = []) {
  let hasDineIn = false;
  let hasTakeaway = false;
  for (const item of items || []) {
    if (getOrderItemType(item) === ITEM_TYPE_TAKEAWAY) hasTakeaway = true;
    else hasDineIn = true;
    if (hasDineIn && hasTakeaway) return true;
  }
  return false;
}

/** Which kitchen tickets to print for this order. */
export function getKitchenPrintPlans(items = [], kitchenPrintOptions = null) {
  const pendingAll = getKitchenPendingItems(items, null);
  if (!pendingAll.length) return [];

  const forced = kitchenPrintOptions?.itemTypeFilter;
  if (forced === ITEM_TYPE_TAKEAWAY || forced === ITEM_TYPE_DINE_IN) {
    const pending = getKitchenPendingItems(items, forced);
    return pending.length ? [{ itemTypeFilter: forced, items: pending }] : [];
  }

  if (orderHasMixedItemTypes(items)) {
    const plans = [];
    const takeawayPending = getKitchenPendingItems(items, ITEM_TYPE_TAKEAWAY);
    const dineInPending = getKitchenPendingItems(items, ITEM_TYPE_DINE_IN);
    if (takeawayPending.length) {
      plans.push({ itemTypeFilter: ITEM_TYPE_TAKEAWAY, items: takeawayPending });
    }
    if (dineInPending.length) {
      plans.push({ itemTypeFilter: ITEM_TYPE_DINE_IN, items: dineInPending });
    }
    return plans;
  }

  return [{ itemTypeFilter: null, items: pendingAll }];
}

export function applyKitchenPrintedMarks(order, printedItems = []) {
  if (!order || !printedItems.length) return order;
  const ids = new Set(
    printedItems.map(
      (i) => i.cartItemId || i.menuItemId || i._id || i.id
    ).filter(Boolean).map(String)
  );
  const items = (order.items || []).map((item) => {
    const key = String(item.cartItemId || item.menuItemId || item._id || item.id || '');
    if (key && ids.has(key)) {
      return { ...item, kitchenPrinted: true, kitchen_printed: true };
    }
    return item;
  });
  return { ...order, items };
}

/** @type {Map<string, Map<string, boolean>>} branchId -> menuItemId -> isCounter */
const counterItemFlagCache = new Map();

function buildCounterItemMapFromMenu(menuItems = []) {
  const map = new Map();
  for (const m of menuItems) {
    const id = String(m._id || m.id || '');
    if (!id) continue;
    map.set(id, !!(m.isCounterItem || m.is_counter_item));
  }
  return map;
}

/**
 * Resolve counter-item flags from POS cache or menu API (remote orders omit flags on line items).
 */
export async function getCounterItemMapForBranch(branchId) {
  if (!branchId) return new Map();
  const key = String(branchId);
  const cached = counterItemFlagCache.get(key);
  if (cached) return cached;

  let menuItems = [];
  try {
    const { default: posDataCache } = await import('../electron/posDataCache');
    const fromCache = posDataCache.getImmediate(key) || posDataCache.get(key);
    menuItems = fromCache?.menuItems || [];
  } catch (_) {
    /* non-Electron / no cache */
  }

  if (!menuItems.length) {
    try {
      const { default: menuService } = await import('./menuService');
      menuItems = await menuService.getMenuItems({ branchId: key });
    } catch (err) {
      console.warn('[print] Could not load menu for counter-item flags:', err?.message || err);
    }
  }

  const map = buildCounterItemMapFromMenu(menuItems);
  counterItemFlagCache.set(key, map);
  return map;
}

/** Call after POS menu reload so counter-item routing picks up menu changes. */
export function invalidateCounterItemFlagCache(branchId) {
  if (branchId) counterItemFlagCache.delete(String(branchId));
  else counterItemFlagCache.clear();
}

export function enrichOrderItemsWithCounterFlags(order, counterMap) {
  if (!order || !counterMap?.size) return order;
  const items = (order.items || order.orderItems || []).map((item) => {
    const menuId = String(item.menuItemId || item.menu_item_id || '');
    const fromMenu = menuId && counterMap.has(menuId) ? counterMap.get(menuId) : undefined;
    const isCounter =
      item.isCounterItem === true ||
      item.is_counter_item === true ||
      fromMenu === true;
    return { ...item, isCounterItem: isCounter };
  });
  return { ...order, items };
}

/**
 * False-positive mod ticket when socket/API snapshots use different line keys but same items.
 */
export const isLikelySnapshotMismatch = (previousItems = [], currentItems = [], changes) => {
  const added = changes?.added || [];
  const removed = changes?.removed || [];
  const quantityChanged = changes?.quantityChanged || [];
  if (removed.length > 0 || quantityChanged.length > 0) return false;
  if (!added.length || added.length !== currentItems.length) return false;
  if (previousItems.length !== currentItems.length) return false;

  const sumQty = (list) =>
    list.reduce((n, i) => n + (parseInt(i.quantity, 10) || 1), 0);
  if (sumQty(previousItems) !== sumQty(currentItems)) return false;

  const namesKey = (list) =>
    list
      .map((i) => `${String(i.name || '').trim().toLowerCase()}x${i.quantity || 1}`)
      .sort()
      .join('|');
  return namesKey(previousItems) === namesKey(currentItems);
};

/**
 * Diff line items for modification kitchen tickets (local + remote print station).
 */
export const diffOrderItemChanges = (originalItems = [], currentItems = []) => {
  const added = [];
  const removed = [];
  const quantityChanged = [];

  currentItems.forEach((item) => {
    const previous = originalItems.find((orig) => normalizeOrderItemId(orig) === normalizeOrderItemId(item));
    if (!previous) {
      added.push({
        name: item.name,
        quantity: item.quantity || 1,
        selectedOptions: item.selectedOptions || [],
        notes: item.notes || '',
        itemType: getOrderItemType(item),
        isCounterItem: isCounterItem(item),
        noPrint: !!item.noPrint,
      });
    } else if ((previous.quantity || 0) !== (item.quantity || 0)) {
      quantityChanged.push({
        name: item.name,
        oldQuantity: previous.quantity || 0,
        newQuantity: item.quantity || 0,
        notes: item.notes || '',
        itemType: getOrderItemType(item),
        isCounterItem: isCounterItem(item),
        noPrint: !!item.noPrint,
      });
    }
  });

  originalItems.forEach((item) => {
    const stillExists = currentItems.find((curr) => normalizeOrderItemId(curr) === normalizeOrderItemId(item));
    if (!stillExists) {
      removed.push({
        name: item.name,
        quantity: item.quantity || 1,
        selectedOptions: item.selectedOptions || [],
        notes: item.notes || '',
        itemType: getOrderItemType(item),
        isCounterItem: isCounterItem(item),
      });
    }
  });

  return { added, removed, quantityChanged };
};

const DEFAULT_PRINT_SERVICE_ORIGIN = 'http://127.0.0.1:3737';

/** Saved ESC/POS code page for this machine's printers ({ byte, encoding }). */
const STORAGE_PRINT_CODEPAGE = 'pos_print_codepage';

export const getSavedPrintCodePage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_PRINT_CODEPAGE);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && Number.isFinite(Number(parsed.byte)) && typeof parsed.encoding === 'string') {
      return { byte: Number(parsed.byte), encoding: parsed.encoding };
    }
  } catch (_) {}
  return null;
};

export const savePrintCodePage = (byte, encoding) => {
  try {
    if (byte == null || !encoding) {
      localStorage.removeItem(STORAGE_PRINT_CODEPAGE);
    } else {
      localStorage.setItem(
        STORAGE_PRINT_CODEPAGE,
        JSON.stringify({ byte: Number(byte), encoding: String(encoding) })
      );
    }
  } catch (_) {}
};

/** Extra payload fields carrying the saved code page (empty when default). */
/** Buzzer/beep on print — opt-in (printers without a buzzer may garble the bytes). */
const STORAGE_PRINT_BEEP = 'pos_print_beep';
export const isPrintBeepEnabled = () => {
  try { return localStorage.getItem(STORAGE_PRINT_BEEP) === 'true'; } catch (_) { return false; }
};
export const setPrintBeepEnabled = (enabled) => {
  try { localStorage.setItem(STORAGE_PRINT_BEEP, enabled ? 'true' : 'false'); } catch (_) {}
};

const printCodePagePayload = () => {
  const cp = getSavedPrintCodePage();
  const out = cp ? { codePageByte: cp.byte, textEncoding: cp.encoding } : {};
  if (isPrintBeepEnabled()) out.beep = true;
  return out;
};

/**
 * Image print mode: render the ticket to PNG in the POS (Chromium canvas has
 * full Unicode) and let the print service print it as a raster image. The only
 * reliable option for printers whose firmware has no Turkish code page.
 */
const STORAGE_PRINT_RENDER_MODE = 'pos_print_render_mode';

export const isImagePrintModeEnabled = () => {
  try {
    const explicit = localStorage.getItem(STORAGE_PRINT_RENDER_MODE);
    if (explicit === 'image') return true;
    if (explicit === 'text') return false;
    // No explicit choice → default to image mode when the RECEIPT language is
    // Turkish (most thermal printers can't render Turkish as text). The receipt
    // language overrides the app language; fall back to the app language
    // (persisted by LanguageContext to localStorage('language')) when unset.
    const receiptLang = getReceiptLanguage() || localStorage.getItem('language');
    return receiptLang === 'tr';
  } catch (_) {
    return false;
  }
};

export const setImagePrintMode = (enabled) => {
  try {
    localStorage.setItem(STORAGE_PRINT_RENDER_MODE, enabled ? 'image' : 'text');
  } catch (_) {}
};

/** 80mm @ 203dpi printable width in dots. */
const RECEIPT_RENDER_WIDTH = 576;
// "Merchant Copy" is a classic thermal-receipt font (used if installed on the
// POS PC); falls back to clean monospaces otherwise.
const RECEIPT_FONT_STACK = '"Merchant Copy", "MerchantCopy", Consolas, "DejaVu Sans Mono", "Courier New", monospace';

/**
 * User-adjustable receipt text size (POS Settings), stored locally per device.
 * 1.0 = default. Clamped 0.7–1.8.
 */
const STORAGE_RECEIPT_FONT_SCALE = 'pos_receipt_font_scale';

export const getReceiptFontScale = () => {
  try {
    const v = parseFloat(localStorage.getItem(STORAGE_RECEIPT_FONT_SCALE));
    if (Number.isFinite(v) && v >= 0.7 && v <= 1.8) return v;
  } catch (_) {}
  return 1;
};

export const setReceiptFontScale = (scale) => {
  try {
    const v = Math.min(1.8, Math.max(0.7, Number(scale) || 1));
    localStorage.setItem(STORAGE_RECEIPT_FONT_SCALE, String(v));
  } catch (_) {}
};

// fx/fy = horizontal/vertical magnification per directive, mirroring ESC/POS:
// normal (1×1), XL = double-height only (fits full-width rows), XXL = double both.
const SCALE_DIMS = { 1: { fx: 1, fy: 1 }, 2: { fx: 1, fy: 2 }, 3: { fx: 2, fy: 2 } };

function parseReceiptRenderLine(raw) {
  const line = String(raw).replace(/\r$/, '');
  if (line.startsWith('>>XXLC>>')) return { text: line.slice(8), align: 'center', scale: 3 };
  if (line.startsWith('>>XXL>>')) return { text: line.slice(7), align: 'right', scale: 3 };
  if (line.startsWith('>>XLC>>')) return { text: line.slice(7), align: 'center', scale: 2 };
  if (line.startsWith('>>XL>>')) return { text: line.slice(6), align: 'left', scale: 2 };
  if (line.startsWith('>>C>>')) return { text: line.slice(5), align: 'center', scale: 1 };
  if (line.startsWith('>>B>>')) return { text: line.slice(5), align: 'center', scale: 1 };
  return { text: line, align: 'left', scale: 1 };
}

/** Render receipt text (with >>C>>/>>XL>> directives) to a PNG base64 string. */
export function renderReceiptTextToPngBase64(content) {
  if (typeof document === 'undefined') return null;
  try {
    const userScale = getReceiptFontScale();
    const basePx = 21 * userScale;   // slightly smaller; 42 monospace chars fit 576 dots
    const baseLh = 30 * userScale;
    const padX = 10;
    const padTop = 14;
    const padBottom = 22;
    const maxWidth = RECEIPT_RENDER_WIDTH - padX * 2;
    const lines = String(content || '').split(/\r?\n/).map(parseReceiptRenderLine);
    const isRule = (s) => /^[-=]{3,}$/.test(String(s).trim());
    // Bold only the biggest headings (CANCELLED etc.); body + item rows stay
    // regular weight so the list isn't heavy.
    const weightFor = (scale) => (scale >= 3 ? 'bold' : 'normal');

    const height = lines.reduce((h, l) => h + baseLh * SCALE_DIMS[l.scale].fy, padTop + padBottom);

    const canvas = document.createElement('canvas');
    canvas.width = RECEIPT_RENDER_WIDTH;
    canvas.height = Math.max(Math.round(height), Math.round(baseLh));
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'top';

    let y = padTop;
    for (const l of lines) {
      const { fx, fy } = SCALE_DIMS[l.scale];
      const rowH = baseLh * fy;
      if (l.text && isRule(l.text)) {
        // Draw a clean horizontal rule instead of printing rows of - or =.
        const isDouble = String(l.text).trim()[0] === '=';
        const cy = Math.round(y + rowH / 2);
        ctx.fillRect(padX, cy - 1, canvas.width - padX * 2, 2);
        if (isDouble) ctx.fillRect(padX, cy + 4, canvas.width - padX * 2, 2);
      } else if (l.text) {
        ctx.font = `${weightFor(l.scale)} ${Math.round(basePx)}px ${RECEIPT_FONT_STACK}`;
        const baseW = ctx.measureText(l.text).width;
        // Horizontal-only shrink when too wide — keeps the vertical emphasis (fy)
        // so an XL row still prints tall even if the line is full width.
        let drawFx = fx;
        let effW = baseW * fx;
        if (effW > maxWidth) {
          drawFx = (maxWidth / baseW);
          effW = maxWidth;
        }
        const x = l.align === 'center'
          ? Math.max(padX, (canvas.width - effW) / 2)
          : l.align === 'right'
            ? Math.max(padX, canvas.width - padX - effW)
            : padX;
        ctx.save();
        ctx.translate(x, y);
        ctx.scale(drawFx, fy);
        ctx.fillText(l.text, 0, 0);
        ctx.restore();
      }
      y += rowH;
    }

    const m = canvas.toDataURL('image/png').match(/^data:image\/png;base64,(.+)$/);
    return m ? m[1] : null;
  } catch (e) {
    console.warn('[print] Receipt image render failed, using text mode:', e?.message || e);
    return null;
  }
}

/** Payload fields for image mode (empty object when disabled or render fails). */
const renderedImagePayload = (content) => {
  if (!isImagePrintModeEnabled()) return {};
  const b64 = renderReceiptTextToPngBase64(content);
  return b64 ? { renderedImageBase64: b64 } : {};
};

/**
 * Transport extras every receipt print should carry: the saved code page AND
 * the rendered image (when image mode is on / Turkish). Use this for any print
 * path so all receipts — kitchen, counter, and bill — handle Turkish identically.
 */
export const buildReceiptPrintExtras = (content) => ({
  ...printCodePagePayload(),
  ...renderedImagePayload(content),
});

export const getPrintServiceUrl = () => {
  try {
    return localStorage.getItem(STORAGE_SERVICE_URL) || DEFAULT_PRINT_SERVICE_ORIGIN;
  } catch (_) {
    return DEFAULT_PRINT_SERVICE_ORIGIN;
  }
};

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode.apply(
      null,
      bytes.subarray(i, Math.min(i + chunk, bytes.length))
    );
  }
  return btoa(binary);
}

function isArrayBufferPng(ab) {
  const bytes = new Uint8Array(ab);
  return (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  );
}

/** Max width for rasterizing branch logo in the POS (print service may have no native `sharp` in pkg). */
/** Keep in sync with print-service LOGO_MAX_WIDTH so pkg builds without sharp still size correctly. */
const BRANCH_LOGO_CANVAS_MAX_WIDTH = 250;

/**
 * Decode JPEG/WebP/etc. to PNG data URL via canvas so the print service only needs pngjs (no sharp).
 */
async function rasterArrayBufferToPngDataUrl(arrayBuffer) {
  if (typeof document === 'undefined') return null;
  const blob = new Blob([arrayBuffer]);
  const url = URL.createObjectURL(blob);
  try {
    let img;
    try {
      img = await createImageBitmap(blob);
    } catch {
      img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error('decode'));
        el.src = url;
      });
    }
    const iw = img instanceof ImageBitmap ? img.width : img.naturalWidth;
    const ih = img instanceof ImageBitmap ? img.height : img.naturalHeight;
    if (!iw || !ih) return null;
    let w = iw;
    let h = ih;
    if (w > BRANCH_LOGO_CANVAS_MAX_WIDTH) {
      h = Math.round((h * BRANCH_LOGO_CANVAS_MAX_WIDTH) / w);
      w = BRANCH_LOGO_CANVAS_MAX_WIDTH;
    }
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    ctx.drawImage(img, 0, 0, w, h);
    if (typeof img.close === 'function') img.close();
    return canvas.toDataURL('image/png');
  } catch {
    return null;
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** In-memory cache so checkout reprints do not re-download branch logo from API. */
const branchLogoBase64Cache = new Map();

/** Warm logo cache when branch loads or checkout opens (non-blocking). */
export function prefetchBranchLogoForPrint(absoluteImageUrl) {
  if (!absoluteImageUrl || typeof absoluteImageUrl !== 'string') return;
  if (branchLogoBase64Cache.has(absoluteImageUrl)) return;
  fetchBranchLogoBase64ForPrint(absoluteImageUrl).catch(() => {});
}

/** Load branch logo bytes with POS auth (print service fetch often gets 401 on /uploads). */
export async function fetchBranchLogoBase64ForPrint(absoluteImageUrl) {
  if (!absoluteImageUrl || typeof absoluteImageUrl !== 'string') return null;
  if (branchLogoBase64Cache.has(absoluteImageUrl)) {
    return branchLogoBase64Cache.get(absoluteImageUrl);
  }
  try {
    const axios = (await import('axios')).default;
    const { getStoredToken } = await import('../utils/authStorage');
    const token = await getStoredToken();
    const res = await axios.get(absoluteImageUrl, {
      responseType: 'arraybuffer',
      timeout: 8000,
      headers: {
        ...(token ? { 'x-auth-token': token } : {}),
        Accept: 'image/*,*/*',
      },
    });
    if (res.status < 200 || res.status >= 300) return null;
    const buf = res.data;
    let out = null;
    if (isArrayBufferPng(buf)) {
      out = arrayBufferToBase64(buf);
    } else {
      const dataUrl = await rasterArrayBufferToPngDataUrl(buf);
      if (dataUrl) {
        const m = dataUrl.match(/^data:image\/png;base64,(.+)$/i);
        if (m) out = m[1];
      }
      if (!out) out = arrayBufferToBase64(buf);
    }
    if (out) branchLogoBase64Cache.set(absoluteImageUrl, out);
    return out;
  } catch (e) {
    console.warn('[print] Branch logo load failed:', e?.message || e);
    return null;
  }
}

function itemHasKitchenDetail(item) {
  if (!item) return false;
  const opts = item.selectedOptions ?? item.selected_options;
  if (Array.isArray(opts) && opts.length > 0) return true;
  return !!String(item.notes ?? item.note ?? item.kitchen_note ?? '').trim();
}

/**
 * Merge local cart snapshot into API order so kitchen slip has options/notes
 * when the server response omits them or the user is on an older backend.
 */
export function mergeOrderForKitchenReceipt(serverOrder, localSnapshot) {
  if (!serverOrder || !localSnapshot?.items?.length) return serverOrder;
  const L = localSnapshot.items;
  const S = serverOrder.items || [];

  if (S.length === 0 && L.length > 0) {
    return {
      ...serverOrder,
      items: L,
      specialInstructions:
        serverOrder.specialInstructions ||
        serverOrder.special_instructions ||
        localSnapshot.specialInstructions ||
        localSnapshot.special_instructions ||
        '',
      kitchenNote: serverOrder.kitchenNote || localSnapshot.kitchenNote || '',
    };
  }

  /** Server lines often drop modifiers; same-length carts → use full local lines for kitchen. */
  const useFullLocalItems =
    L.length > 0 &&
    S.length === L.length &&
    S.some((si, i) => itemHasKitchenDetail(L[i]) && !itemHasKitchenDetail(si));

  if (useFullLocalItems) {
    return {
      ...serverOrder,
      items: L,
      specialInstructions:
        serverOrder.specialInstructions ||
        serverOrder.special_instructions ||
        localSnapshot.specialInstructions ||
        localSnapshot.special_instructions ||
        '',
      kitchenNote: serverOrder.kitchenNote || localSnapshot.kitchenNote || '',
    };
  }

  const merged = S.map((si, i) => {
    const byCart =
      si?.cartItemId != null
        ? L.find((x) => x != null && String(x.cartItemId) === String(si.cartItemId))
        : null;
    const byMenu =
      si?.menuItemId != null
        ? L.find((x) => x != null && String(x.menuItemId) === String(si.menuItemId))
        : null;
    const li = byCart || L[i] || byMenu || null;
    if (!li) return si;
    const serverOpts = si.selectedOptions ?? si.selected_options;
    const localOpts = li.selectedOptions ?? li.selected_options;
    const opts =
      Array.isArray(serverOpts) && serverOpts.length > 0
        ? serverOpts
        : Array.isArray(localOpts) && localOpts.length > 0
          ? localOpts
          : [];
    const serverNote = String(si.notes ?? si.note ?? si.kitchen_note ?? '').trim();
    const localNote = String(li.notes ?? li.note ?? li.kitchen_note ?? '').trim();
    const notes = serverNote || localNote || '';
    const counterFromLocal =
      li.isCounterItem === true || li.is_counter_item === true;
    const counterFromServer =
      si.isCounterItem === true || si.is_counter_item === true;
    const itemType =
      si.itemType === 'takeaway' || si.item_type === 'takeaway'
        ? ITEM_TYPE_TAKEAWAY
        : li.itemType === 'takeaway' || li.item_type === 'takeaway'
          ? ITEM_TYPE_TAKEAWAY
          : ITEM_TYPE_DINE_IN;
    return {
      ...si,
      ...(Array.isArray(opts) && opts.length ? { selectedOptions: opts } : {}),
      ...(notes ? { notes } : {}),
      isCounterItem: counterFromServer || counterFromLocal,
      itemType,
    };
  });
  return {
    ...serverOrder,
    items: merged,
    specialInstructions:
      serverOrder.specialInstructions ||
      serverOrder.special_instructions ||
      localSnapshot.specialInstructions ||
      localSnapshot.special_instructions ||
      '',
    kitchenNote: serverOrder.kitchenNote || localSnapshot.kitchenNote || '',
  };
}

/** Auth for fetching branch logo URL on the print service (Node fetch). Send whenever logoUrl is https so base64 can fail over to URL. */
async function buildLogoFetchHeaders(logoUrl) {
  if (!logoUrl || typeof logoUrl !== 'string') return null;
  const u = logoUrl.trim();
  if (!u.startsWith('http://') && !u.startsWith('https://')) return null;
  try {
    const { getStoredToken } = await import('../utils/authStorage');
    const token = await getStoredToken();
    return token ? { 'x-auth-token': token } : null;
  } catch {
    return null;
  }
}

/**
 * fetch() from Electron file:// pages is often blocked to http://127.0.0.1; main process proxies when available.
 */
export async function printServiceFetch(url, init = {}) {
  const electron = typeof window !== 'undefined' && window.electronAPI?.printServiceRequest;
  if (electron) {
    const r = await electron({
      url: String(url),
      method: init.method || 'GET',
      headers: init.headers || {},
      body: init.body,
    });
    if (r.networkError) {
      throw new Error(r.networkError);
    }
    return {
      ok: !!r.ok,
      status: r.status,
      json: async () => {
        if (r.jsonData !== undefined) return r.jsonData;
        return JSON.parse(r.text || '');
      },
      text: async () => r.text || '',
    };
  }
  return fetch(url, init);
}

const line = (char = '-') => char.repeat(THERMAL_80MM_CHARS);
const cell = (s, w, align = 'left') => {
  const str = String(s ?? '');
  const clipped = str.length > w ? str.slice(0, w) : str;
  return align === 'right' ? clipped.padStart(w, ' ') : clipped.padEnd(w, ' ');
};
const kitchenTableHeaderRow = (t) => {
  const nameLbl = (t ? t('pos.item') : 'Item').slice(0, KITCHEN_COL_NAME);
  const qtyLbl = (t ? t('pos.qty') : 'Qty').slice(0, KITCHEN_COL_QTY);
  return `${cell(nameLbl, KITCHEN_COL_NAME)} ${cell(qtyLbl, KITCHEN_COL_QTY, 'right')}`;
};
const kitchenTableRow = (name, qty) => {
  return `${cell(name, KITCHEN_COL_NAME)} ${cell(String(qty || 1), KITCHEN_COL_QTY, 'right')}`;
};
const getKitchenNoteValue = (order) => {
  const raw =
    order?.kitchenNote ??
    order?.note ??
    order?.notes ??
    order?.specialInstructions ??
    order?.special_instructions ??
    '';
  const cleaned = String(raw).trim();
  return cleaned || '';
};

const getItemOptionsList = (item) => item?.selectedOptions ?? item?.selected_options ?? [];
const getItemNoteText = (item) => {
  const n = item?.notes ?? item?.note ?? item?.kitchen_note ?? '';
  return String(n || '').trim();
};
/** Order-level kitchen note on thermal slip (no "Kitchen Note:" label). */
const formatOrderKitchenNoteLine = (note) => {
  const text = String(note || '').trim();
  if (!text) return null;
  return text.slice(0, THERMAL_80MM_CHARS);
};
/** Per-item note on thermal slip (indented, no label prefix). */
const formatItemKitchenNoteLine = (note) => {
  const text = String(note || '').trim();
  if (!text) return null;
  return `  > ${text.slice(0, THERMAL_80MM_CHARS - 4)}`;
};
const formatKitchenOptionLine = (opt) => {
  const title = opt?.title ?? opt?.groupTitle ?? opt?.group_title ?? 'Option';
  const choice = opt?.choice ?? opt?.label ?? opt?.value ?? '';
  return `  > ${title}: ${choice}`;
};

const isCounterItem = (item) => !!(item?.isCounterItem || item?.is_counter_item);

/**
 * Display order number: T0037 (takeaway) / D0012 (delivery) / R0043 (dine-in),
 * derived from order_number + orderType. Prefers the backend-formatted value
 * when present (create response), else derives it (works on re-fetched orders
 * where formattedOrderNumber isn't persisted).
 */
export const formatDisplayOrderNumber = (order) => {
  if (order?.formattedOrderNumber) return order.formattedOrderNumber;
  const raw = order?.orderNumber ?? order?.order_number;
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return String(raw); // offline temp like "L-0001"
  const prefix = order?.orderType === 'delivery' ? 'D' : order?.orderType === 'takeaway' ? 'T' : 'R';
  return `${prefix}${String(n).padStart(4, '0')}`;
};

// ─── Kitchen ticket label memory ─────────────────────────────────────────────
// The FIRST kitchen slip for an order fixes its header label; every later slip
// (modification, cancellation) must show the SAME label. Without this, a
// takeaway printed through the 1.2s early-print gate (before the server
// assigned order_number) went out with the virtual-slot label ("TAKEAWAY: 2"),
// while the modification slip later showed the real number ("TAKEAWAY: T1351")
// — kitchen staff couldn't match the two (Istanbul, 2026-08-01 report).
// Keyed by every identity the order has (server id, localId, provisional
// ticket key) so the early-printed label survives the local→server id swap.
const KITCHEN_LABEL_STORE_KEY = 'pos_kitchen_ticket_labels_v1';
const KITCHEN_LABEL_MAX_ENTRIES = 300;

const orderLabelKeys = (order) => {
  const keys = [
    order?._kitchenTicketKey,
    order?._id,
    order?.id,
    order?.localId,
    order?.local_id,
  ];
  return keys.filter(Boolean).map(String);
};

const readKitchenLabelStore = () => {
  try {
    const raw = localStorage.getItem(KITCHEN_LABEL_STORE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (_) { return {}; }
};

const writeKitchenLabelStore = (store) => {
  try {
    const entries = Object.entries(store);
    if (entries.length > KITCHEN_LABEL_MAX_ENTRIES) {
      entries.sort((a, b) => (a[1]?.at || 0) - (b[1]?.at || 0));
      store = Object.fromEntries(entries.slice(entries.length - KITCHEN_LABEL_MAX_ENTRIES));
    }
    localStorage.setItem(KITCHEN_LABEL_STORE_KEY, JSON.stringify(store));
  } catch (_) { /* quota — labels are a nicety, never break printing */ }
};

export const getStoredKitchenLabel = (order) => {
  const store = readKitchenLabelStore();
  for (const key of orderLabelKeys(order)) {
    const hit = store[key];
    if (hit?.label) return hit.label;
  }
  return null;
};

export const storeKitchenLabel = (order, label) => {
  if (!label) return;
  const keys = orderLabelKeys(order);
  if (!keys.length) return;
  const store = readKitchenLabelStore();
  const at = Date.now();
  keys.forEach((key) => { store[key] = { label, at }; });
  writeKitchenLabelStore(store);
};

/**
 * After an early-printed order's create resolves, copy the label stored under
 * the provisional keys onto the real server id so modification slips find it.
 */
export const rememberKitchenLabelForOrder = (order) => {
  const label = getStoredKitchenLabel(order);
  if (label) storeKitchenLabel(order, label);
};

/**
 * Header number for takeaway/delivery kitchen slips: sticky-first. Whatever
 * the first printed slip showed is reused verbatim on all later slips.
 */
const resolveKitchenHeaderNumber = (order, table, na) => {
  const orderIsTakeaway = order?.orderType === 'takeaway';
  const orderIsDelivery = order?.orderType === 'delivery';
  if (!orderIsTakeaway && !orderIsDelivery) {
    return table?.number || table?.displayName || na;
  }
  const stored = getStoredKitchenLabel(order);
  if (stored) return stored;
  const derived = formatDisplayOrderNumber(order) || table?.number || table?.displayName || na;
  if (derived !== na) storeKitchenLabel(order, derived);
  return derived;
};

export const buildKitchenReceiptText = (
  order,
  table,
  t,
  language = null,
  timezone = null,
  titleOverride = null,
  options = {}
) => {
  const { itemTypeFilter = null, itemsOverride = null } = options;
  const items = itemsOverride || order.items || [];
  const isTakeawayTicket = itemTypeFilter === ITEM_TYPE_TAKEAWAY;
  const takeawayLabel = t ? t('pos.takeaway') : 'TAKEAWAY';
  const na = t ? t('pos.receiptNotAvailable') : 'N/A';
  const kitchenNote = getKitchenNoteValue(order);
  const { dateStr, timeStr } = getReceiptDateTimeStrings(language, timezone);
  // Header label: solo takeaway/delivery (virtual table) → "TAKEAWAY:" / "DELIVERY:".
  const isDeliveryHeader = table?.section === 'Delivery' || order?.orderType === 'delivery';
  const isTakeawayHeader = isTakeawayTicket || (!isDeliveryHeader && (table?.isVirtual || table?.section === 'Takeaway' || order?.orderType === 'takeaway'));
  const headerLabel = isDeliveryHeader
    ? 'DELIVERY'
    : isTakeawayHeader
      ? 'TAKEAWAY'
      : (t ? t('pos.table') : 'Table');
  // Number source by the ORDER's real type:
  //  - genuine takeaway/delivery order → T0037 / D-number (sticky: first
  //    printed label is reused on every later slip for this order)
  //  - dine-in order (incl. its takeaway portion) → physical table number
  const headerNumber = resolveKitchenHeaderNumber(order, table, na);
  return [
    line('='),
    `>>XLC>>${titleOverride || (t ? t('pos.kitchenOrder') : 'KITCHEN ORDER')}`,
    line('='),
    `>>XLC>>${headerLabel}: ${headerNumber}`,
    ...(isTakeawayTicket
      ? [`>>B>>${takeawayLabel.toUpperCase()}`]
      : []),
    `${t ? t('pos.receiptDate') : 'Date:'} ${dateStr}`,
    `${t ? t('pos.receiptTime') : 'Time:'} ${timeStr}`,
    ...(kitchenNote
      ? [
          line('-'),
          formatOrderKitchenNoteLine(kitchenNote),
        ]
      : []),
    line('-'),
    kitchenTableHeaderRow(t),
    line('-'),
    ...(items.length
      ? items.flatMap((item) => {
          // >>XL>> = double-height so kitchen staff can read item + qty at a glance.
          const rows = [kitchenTableRow(item.name || (t ? t('pos.item') : 'Item'), item.quantity || 1)];
          const opts = getItemOptionsList(item);
          if (Array.isArray(opts) && opts.length > 0) {
            opts.forEach((opt) => rows.push(formatKitchenOptionLine(opt)));
          }
          const itemNote = getItemNoteText(item);
          if (itemNote) {
            const noteLine = formatItemKitchenNoteLine(itemNote);
            if (noteLine) rows.push(noteLine);
          }
          rows.push('');
          return rows;
        })
      : [t ? t('pos.noItems') : 'No items']),
    line('='),
  ].join('\n');
};

const filterModificationChangesByItemType = (changes, itemTypeFilter) => {
  if (!itemTypeFilter || !changes) return changes;
  const match = (item) => getOrderItemType(item) === itemTypeFilter;
  return {
    added: (changes.added || []).filter(match),
    removed: (changes.removed || []).filter(match),
    quantityChanged: (changes.quantityChanged || []).filter(match),
  };
};

const filterModificationChangesByCounter = (changes, wantCounter) => {
  if (!changes) {
    return { added: [], removed: [], quantityChanged: [] };
  }
  const match = (item) => isCounterItem(item) === wantCounter;
  return {
    added: (changes.added || []).filter(match),
    removed: (changes.removed || []).filter(match),
    quantityChanged: (changes.quantityChanged || []).filter(match),
  };
};

const modificationChangesEmpty = (changes) =>
  !(changes?.added?.length || changes?.removed?.length || changes?.quantityChanged?.length);

/**
 * Drop items flagged "no print" from the PRINTED changes — without touching the
 * diff itself. The item stays on the order (so it's never seen as removed → no
 * false CANCELLED ticket); it's simply omitted from the kitchen ticket lines.
 */
const filterModificationChangesNoPrint = (changes) => ({
  added: (changes?.added || []).filter((c) => !c?.noPrint),
  quantityChanged: (changes?.quantityChanged || []).filter((c) => !c?.noPrint),
  removed: (changes?.removed || []).filter((c) => !c?.noPrint),
});

/**
 * Split a change set into:
 *  - additions:     added items + quantity INCREASES  → "MODIFICATION" ticket
 *  - cancellations: removed items + quantity DECREASES → "CANCELLED" ticket
 * so each prints on its own kitchen receipt.
 */
const splitAdditionsCancellations = (changes) => {
  const qc = changes?.quantityChanged || [];
  const increases = qc.filter((i) => (Number(i.newQuantity) || 0) > (Number(i.oldQuantity) || 0));
  const decreases = qc.filter((i) => (Number(i.newQuantity) || 0) < (Number(i.oldQuantity) || 0));
  return {
    additions: { added: changes?.added || [], removed: [], quantityChanged: increases },
    cancellations: { added: [], removed: changes?.removed || [], quantityChanged: decreases },
  };
};

async function printModificationChangeSlice({
  order,
  table,
  changes,
  t,
  language,
  timezone,
  itemTypeFilter,
  titleOverride,
  sendFn,
}) {
  if (modificationChangesEmpty(changes)) return false;

  const { additions, cancellations } = splitAdditionsCancellations(changes);
  let printedAny = false;

  // Additions / quantity increases → MODIFICATION (RUNNING ORDER) ticket.
  if (!modificationChangesEmpty(additions)) {
    const ticket = generateModificationKitchenTicket(
      order, table, additions, t, language, timezone,
      { itemTypeFilter, titleOverride }
    );
    await sendFn(ticket);
    printedAny = true;
  }

  // Removals / quantity decreases → SEPARATE ticket with big CANCELLED heading.
  if (!modificationChangesEmpty(cancellations)) {
    const ticket = generateModificationKitchenTicket(
      order, table, cancellations, t, language, timezone,
      { itemTypeFilter, titleOverride, headingMode: 'cancelled' }
    );
    await sendFn(ticket);
    printedAny = true;
  }

  return printedAny;
}

export const generateModificationKitchenTicket = (
  order,
  table,
  changes,
  t,
  language = null,
  timezone = null,
  options = {}
) => {
  const itemTypeFilter = options?.itemTypeFilter || null;
  const titleOverride = options?.titleOverride || null;
  const headingMode = options?.headingMode || null; // 'cancelled' → big CANCELLED heading
  const filtered = filterModificationChangesByItemType(changes, itemTypeFilter);
  const added = filtered?.added || [];
  const removed = filtered?.removed || [];
  const quantityChanged = filtered?.quantityChanged || [];
  const na = t ? t('pos.receiptNotAvailable') : 'N/A';
  const modTitle = t ? t('pos.receiptModificationTitle') : 'Running';
  const baseTitle = headingMode === 'cancelled'
    ? ((t && t('pos.cancelled')) ? String(t('pos.cancelled')).toUpperCase() : 'CANCELLED')
    : modTitle;
  // Cancelled tickets show just "CANCELLED" big on top (don't prefix with the
  // COUNTER/section title); modification tickets keep the optional prefix.
  const heading = headingMode === 'cancelled'
    ? baseTitle
    : (titleOverride ? `${titleOverride} — ${baseTitle}` : baseTitle);
  const takeawayLabel = t ? t('pos.takeaway') : 'TAKEAWAY';
  const itemFallback = t ? t('pos.item') : 'Item';
  const kitchenNote = getKitchenNoteValue(order);
  const { dateStr, timeStr } = getReceiptDateTimeStrings(language, timezone);
  const isDeliveryHeader = table?.section === 'Delivery' || order?.orderType === 'delivery';
  const isTakeawayHeader = itemTypeFilter === ITEM_TYPE_TAKEAWAY || (!isDeliveryHeader && (table?.isVirtual || table?.section === 'Takeaway' || order?.orderType === 'takeaway'));
  const headerLabel = isDeliveryHeader ? 'DELIVERY' : isTakeawayHeader ? 'TAKEAWAY' : (t ? t('pos.table') : 'Table');
  // Sticky label: the modification slip must carry the SAME number the
  // original kitchen slip printed (Istanbul mismatch report).
  const headerNumber = resolveKitchenHeaderNumber(order, table, na);
  // Cancelled heading is extra-large (double width+height centered); modification
  // heading stays double-height centered.
  const headingDirective = headingMode === 'cancelled' ? '>>XXLC>>' : '>>XLC>>';
  const lines = [
    line('='),
    `${headingDirective}${heading}`,
    line('='),
    `>>XLC>>${headerLabel}: ${headerNumber}`,
    ...(itemTypeFilter === ITEM_TYPE_TAKEAWAY
      ? [`>>B>>${takeawayLabel.toUpperCase()}`]
      : []),
    `${t ? t('pos.receiptDate') : 'Date:'} ${dateStr} | ${t ? t('pos.receiptTime') : 'Time:'} ${timeStr}`,
    line('-'),
  ];

  if (kitchenNote) {
    const noteLine = formatOrderKitchenNoteLine(kitchenNote);
    if (noteLine) lines.push(noteLine);
    lines.push(line('-'));
  }

  if (added.length) {
    lines.push(t ? t('pos.receiptAdded') : 'ADDED:');
    lines.push(kitchenTableHeaderRow(t));
    lines.push(line('-'));
    added.forEach((item) => {
      // Column format like the kitchen order: name left, qty right (no "x").
      lines.push(kitchenTableRow(item.name || itemFallback, item.quantity || 1));
      const opts = getItemOptionsList(item);
      if (Array.isArray(opts) && opts.length > 0) {
        opts.forEach((opt) => lines.push(formatKitchenOptionLine(opt)));
      }
      const itemNote = getItemNoteText(item);
      if (itemNote) {
        const noteLine = formatItemKitchenNoteLine(itemNote);
        if (noteLine) lines.push(noteLine);
      }
    });
  }

  if (removed.length) {
    if (added.length) lines.push(line('-'));
    lines.push(t ? t('pos.receiptRemoved') : 'REMOVED:');
    lines.push(kitchenTableHeaderRow(t));
    lines.push(line('-'));
    removed.forEach((item) => {
      lines.push(kitchenTableRow(item.name || itemFallback, item.quantity || 1));
      const itemNote = getItemNoteText(item);
      if (itemNote) {
        const noteLine = formatItemKitchenNoteLine(itemNote);
        if (noteLine) lines.push(noteLine);
      }
    });
  }

  if (quantityChanged.length) {
    if (added.length || removed.length) lines.push(line('-'));
    lines.push(t ? t('pos.receiptQtyChanged') : 'QTY CHANGED:');
    lines.push(kitchenTableHeaderRow(t));
    lines.push(line('-'));
    quantityChanged.forEach((item) => {
      // Delta in the qty column (e.g. +2 / -1), name on the left — like the kitchen order.
      const delta = (Number(item.newQuantity) || 0) - (Number(item.oldQuantity) || 0);
      const deltaStr = delta > 0 ? `+${delta}` : `${delta}`;
      lines.push(kitchenTableRow(item.name || itemFallback, deltaStr));
      const itemNote = getItemNoteText(item);
      if (itemNote) {
        const noteLine = formatItemKitchenNoteLine(itemNote);
        if (noteLine) lines.push(noteLine);
      }
    });
  }

  lines.push(line('='));

  return lines.join('\n');
};

/**
 * ESC/POS text for branch sales report (80mm). Uses same directives as receipts.
 * @param {object} data     - API payload from GET /reports/branch-sales
 * @param {object} labels   - UI strings (i18n)
 * @param {string} timezone - IANA timezone for the "Printed at" timestamp
 */
export const buildBranchSalesReportThermalText = (data, labels = {}, timezone = null) => {
  const W = THERMAL_80MM_CHARS;
  const rule = (ch = '=') => ch.repeat(W);
  const rightPair = (label, value) => {
    const l = String(label);
    const r = String(value);
    const sp = Math.max(1, W - l.length - r.length);
    return `${l}${' '.repeat(sp)}${r}`.slice(0, W);
  };

  const cur = data.currency || 'USD';
  const money = (n) => formatReceiptCurrency(n, cur);

  const branch = labels.branchName || data.branchName || '';
  const range = data.reportRange || {};
  const startD = range.startDate || '';
  const endD = range.endDate || '';
  // Use branch timezone for the "printed at" time so it matches the branch clock
  const tzOpts = timezone ? { timeZone: timezone } : {};
  const printed = labels.printedAt
    || (() => {
      try { return new Date().toLocaleString(undefined, tzOpts); } catch { return new Date().toLocaleString(); }
    })();

  const pm = data.paymentMethods || {};
  const salesCash = data.salesCash != null ? data.salesCash : pm.cash || 0;
  const salesCard =
    data.salesCard != null
      ? data.salesCard
      : (parseFloat(pm.card) || 0) +
        (parseFloat(pm.mobile) || 0) +
        (parseFloat(pm.qr) || 0) +
        (parseFloat(pm.other) || 0);

  const expCash = data.expenseCash != null ? data.expenseCash : 0;
  const expCard = data.expenseCard != null ? data.expenseCard : 0;
  const disc = data.totalDiscount != null ? data.totalDiscount : 0;
  const otc = data.orderTypeCounts || {};
  const del = data.deletedOrders || { count: 0, totalAmount: 0 };
  const cats = Array.isArray(data.salesByCategory) ? data.salesByCategory : [];

  const lines = [
    rule('='),
    `>>C>>${labels.title || 'SALES REPORT'}`,
    `>>C>>${branch}`,
    rule('-'),
    `${labels.period || 'Period'}: ${startD} - ${endD}`,
    `${labels.printed || 'Printed'}: ${printed}`,
    rule('='),
    `>>XL>>${labels.salesSection || 'SALES'}`,
    rule('-'),
    rightPair(labels.totalSales || 'Total sales', money(data.totalSales || 0)),
    rightPair(labels.cash || 'Cash', money(salesCash)),
    rightPair(labels.card || 'Card', money(salesCard)),
    `>>C>>${labels.cardNote || 'Card incl. mobile & QR'}`,
    rightPair(labels.totalDiscount || 'Total discount', money(disc)),
    '',
    `>>XL>>${labels.expensesSection || 'EXPENSES'}`,
    rule('-'),
    rightPair(labels.totalExpenses || 'Total expenses', money(data.totalExpenses || 0)),
    rightPair(labels.cash || 'Cash', money(expCash)),
    rightPair(labels.card || 'Card', money(expCard)),
    '',
    `>>XL>>${labels.ordersSection || 'ORDERS'}`,
    rule('-'),
    rightPair(labels.orderCount || 'No. of orders', String(data.totalOrders || 0)),
    rightPair(labels.takeaways || 'Takeaways', String(otc.takeaway ?? 0)),
    rightPair(labels.tables || 'Table orders', String(otc.dineIn ?? 0)),
  ];

  if ((otc.delivery || 0) > 0) {
    lines.push(rightPair(labels.delivery || 'Delivery', String(otc.delivery)));
  }

  lines.push(
    '',
    `>>XL>>${labels.byCategory || 'BY CATEGORY'}`,
    rule('-')
  );

  if (cats.length === 0) {
    lines.push(labels.noCategories || 'No category data');
  } else {
    cats.forEach((c) => {
      const name = String(c.category || '').slice(0, 18);
      lines.push(rightPair(name, money(c.totalSales || 0)));
    });
  }

  lines.push(
    '',
    `>>XL>>${labels.deletedSection || 'DELETED ORDERS'}`,
    rule('-'),
    rightPair(labels.deletedCount || 'Count', String(del.count || 0)),
    rightPair(labels.deletedValue || 'Total value', money(del.totalAmount || 0)),
    rule('='),
    `>>C>>${labels.footer || 'End of report'}`,
    rule('=')
  );

  return lines.join('\n');
};

/**
 * Build a thermal receipt for the POS Expenses tab — just title + amount for
 * each expense of a single day, plus a total line. Purposely minimal (no
 * category / payment method / notes) per the "title and price only" spec.
 *
 * @param {object} params
 * @param {Array}  params.expenses  [{ title, price }]
 * @param {string} params.branchName
 * @param {string} params.currency
 * @param {string} params.dateLabel  e.g. "2026-07-20"
 * @param {object} labels            optional translated headings
 */
export const buildExpenseReportThermalText = (
  { expenses = [], branchName = '', currency = 'USD', dateLabel = '' },
  labels = {}
) => {
  const W = THERMAL_80MM_CHARS;
  const rule = (ch = '=') => ch.repeat(W);
  const money = (n) => formatReceiptCurrency(n, currency);
  const rightPair = (label, value) => {
    const l = String(label);
    const r = String(value);
    const sp = Math.max(1, W - l.length - r.length);
    return `${l}${' '.repeat(sp)}${r}`.slice(0, W);
  };

  const printed = (() => {
    try { return new Date().toLocaleString(); } catch { return new Date().toISOString(); }
  })();

  const total = (expenses || []).reduce(
    (s, e) => s + (parseFloat(e.price) || 0),
    0
  );

  const lines = [
    rule('='),
    `>>C>>${labels.title || 'EXPENSE REPORT'}`,
    ...(branchName ? [`>>C>>${branchName}`] : []),
    rule('-'),
    `${labels.date || 'Date'}: ${dateLabel}`,
    `${labels.printed || 'Printed'}: ${printed}`,
    rule('='),
    rightPair(labels.itemHeader || 'Item', labels.amountHeader || 'Amount'),
    rule('-'),
  ];

  if (!expenses || expenses.length === 0) {
    lines.push(`>>C>>${labels.none || 'No expenses for this date'}`);
  } else {
    expenses.forEach((e) => {
      // Truncate long titles so the amount doesn't wrap off the paper —
      // the amount column takes ~12 chars, leaving ~28 for the title.
      const titleCol = String(e.title || '').slice(0, W - 14);
      lines.push(rightPair(titleCol, money(parseFloat(e.price) || 0)));
    });
  }

  lines.push(
    rule('-'),
    rightPair(labels.total || 'Total', money(total)),
    rule('='),
    ''
  );

  return lines.join('\n');
};

/** Same printer as POS counter receipt (USB or IP from POS settings). */
// ── Print retry queue ─────────────────────────────────────────────────────────
// A kitchen/counter ticket that fails (printer powered off, LAN flap — see the
// EHOSTUNREACH failures at Ayder) is queued and retried until the printer
// answers again: a ticket must never be permanently lost to a 30-second
// network blip. In-memory only — an app restart drops the queue, but restarts
// mid-service are rare and any ticket can be reprinted from Order History.
const PRINT_RETRY_INTERVAL_MS = 20_000;
const PRINT_RETRY_MAX_ATTEMPTS = 15; // ≈5 minutes of retrying
const _printRetryQueue = [];
let _printRetryTimer = null;

/**
 * Surface a kitchen-print failure to the UI (POSScreen listens and shows a
 * compulsory red modal). Managers were finding out an hour later that the
 * kitchen never saw an order because the printer was off — a silent retry
 * queue is not enough; staff must be told the moment the ticket fails.
 */
function notifyKitchenPrintFailure(detail) {
  try {
    window.dispatchEvent(new CustomEvent('pos-kitchen-print-failed', { detail }));
  } catch (_) { /* non-fatal */ }
}

function enqueuePrintRetry(kind, args, err) {
  _printRetryQueue.push({
    kind,
    args,
    attempts: 0,
    firstError: String(err?.message || err),
    queuedAt: Date.now(),
  });
  console.warn('[print] queued for retry', {
    kind,
    queueLength: _printRetryQueue.length,
    ...(args.printMeta || {}),
  });
  if (!_printRetryTimer) {
    _printRetryTimer = setInterval(_drainPrintRetryQueue, PRINT_RETRY_INTERVAL_MS);
  }
}

async function _drainPrintRetryQueue() {
  if (_printRetryQueue.length === 0) {
    clearInterval(_printRetryTimer);
    _printRetryTimer = null;
    return;
  }
  const entry = _printRetryQueue[0];
  entry.attempts += 1;
  const retryMeta = { ...(entry.args.printMeta || {}), __isRetry: true };
  try {
    if (entry.kind === 'kitchen') {
      await sendToKitchenPrinterOnce(entry.args.content, entry.args.logoUrl, retryMeta);
    } else {
      await sendToCounterPrinterOnce(entry.args.content, entry.args.logoUrl, entry.args.logoBase64, retryMeta);
    }
    _printRetryQueue.shift();
    console.warn('[print] retry SUCCESS — queued ticket printed', {
      kind: entry.kind,
      attempts: entry.attempts,
      queuedForMs: Date.now() - entry.queuedAt,
      ...(entry.args.printMeta || {}),
    });
    // Auto-clear the failure modal if it's still showing for a recoverable
    // failure — the ticket DID print now, no reason to keep alarming staff.
    if (entry.kind === 'kitchen') {
      notifyKitchenPrintFailure({ stage: 'recovered', orderNo: entry.args.printMeta?.orderNo ?? null });
    }
  } catch (err) {
    if (entry.attempts >= PRINT_RETRY_MAX_ATTEMPTS) {
      _printRetryQueue.shift();
      console.error('[print] retry GAVE UP — ticket NOT printed, reprint manually from Order History', {
        kind: entry.kind,
        attempts: entry.attempts,
        firstError: entry.firstError,
        ...(entry.args.printMeta || {}),
      });
      // Final escalation: the ticket is now permanently unprinted — staff must
      // reprint from Order History once the printer is back.
      if (entry.kind === 'kitchen') {
        notifyKitchenPrintFailure({
          stage: 'gave-up',
          errMsg: entry.firstError,
          orderNo: entry.args.printMeta?.orderNo ?? null,
          table: entry.args.printMeta?.table ?? null,
          willRetry: false,
        });
      }
    }
    // else: keep it queued for the next interval
  }
}

const sendToCounterPrinterOnce = async (content, logoUrl = null, logoBase64 = null, printMeta = null) => {
  const usbPrinter = getSavedCounterUsb();
  const ipPrinter = getSavedCounterIp();
  const baseUrl = getPrintServiceUrl();
  const _printer = usbPrinter ? `usb:${usbPrinter}` : ipPrinter ? `ip:${ipPrinter}` : 'none';
  console.warn('[print] counter attempt', { printer: _printer, bytes: content?.length || 0, ...(printMeta || {}) });
  const hasEmbeddedLogo = !!logoBase64;
  const payloadLogoUrl = hasEmbeddedLogo ? undefined : logoUrl || undefined;
  const printBody = {
    data: content,
    logoUrl: payloadLogoUrl,
    logoBase64: logoBase64 || undefined,
    type: 'TEXT',
    receiptKind: 'counter',
    includeFooterLogo: false,
    ...printCodePagePayload(),
    ...renderedImagePayload(content),
  };

  if (usbPrinter) {
    const res = await printServiceFetch(`${baseUrl}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        printerName: usbPrinter,
        ...printBody,
      }),
    });
    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({}));
      const msg = errorBody.message || errorBody.error || 'Counter USB print failed';
      console.error('[print] counter USB FAILED', { printer: _printer, status: res.status, msg });
      throw new Error(msg);
    }
    console.warn('[print] counter USB success', { printer: _printer });
    return;
  }

  if (ipPrinter) {
    const res = await printServiceFetch(`${baseUrl}/print-ip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ip: ipPrinter,
        port: 9100,
        ...printBody,
      }),
    });
    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({}));
      const msg = errorBody.message || errorBody.error || 'Counter IP print failed';
      console.error('[print] counter IP FAILED', { printer: _printer, status: res.status, msg });
      throw new Error(msg);
    }
    console.warn('[print] counter IP success', { printer: _printer });
    return;
  }

  console.error('[print] no counter printer configured');
  throw new Error('No counter printer configured. Set it in POS settings.');
};

/** Public counter-print entry — failed tickets are queued and retried. */
export const sendToCounterPrinter = async (content, logoUrl = null, logoBase64 = null, printMeta = null) => {
  try {
    return await sendToCounterPrinterOnce(content, logoUrl, logoBase64, printMeta);
  } catch (err) {
    const isConfigError = String(err?.message || '').includes('printer configured');

    // COUNTER→KITCHEN FALLBACK. The Ayder counter printer died 4 days in a
    // row (Aug 4/7/8: Tables 21, 22, 1, 23 — slips permanently lost after the
    // 5-min retry window, staff reported "missed orders"). A dead counter
    // printer must never cost a ticket: print it on the KITCHEN printer
    // immediately with a banner so staff route it by hand. Only on the FIRST
    // failure (not retries) and never for missing-config (that's a setup
    // problem the not-configured alert already covers).
    if (!printMeta?.__isRetry && !isConfigError) {
      try {
        const banner = [
          '>>XLC>>*** COUNTER PRINTER DOWN ***',
          '>>B>>Ticket rerouted to kitchen printer',
          '================================',
        ].join('\n');
        await sendToKitchenPrinter(`${banner}\n${content}`, logoUrl, {
          ...(printMeta || {}),
          __isRetry: true, // never re-enqueue or re-fallback from this send
          __counterFallback: true,
        });
        console.warn('[print] counter FAILED — ticket printed on KITCHEN printer instead', {
          ...(printMeta || {}),
        });
        return; // ticket is physically out; do NOT queue a counter retry (would duplicate)
      } catch (fallbackErr) {
        // Kitchen also unreachable (e.g. whole LAN down) — fall through to the
        // normal counter retry queue so the ticket still prints when it recovers.
        console.warn('[print] counter fallback to kitchen ALSO failed — queueing counter retry', {
          errMsg: String(fallbackErr?.message || fallbackErr),
        });
      }
      enqueuePrintRetry('counter', { content, logoUrl, logoBase64, printMeta }, err);
    }
    throw err;
  }
};

export { buildLogoFetchHeaders };

const sendToKitchenPrinterOnce = async (content, logoUrl = null, printMeta = null) => {
  const usbPrinter = getSavedKitchenUsb();
  const ipPrinter = getSavedKitchenIp();
  const baseUrl = getPrintServiceUrl();
  const imagePayload = renderedImagePayload(content);
  const _printer = usbPrinter ? `usb:${usbPrinter}` : ipPrinter ? `ip:${ipPrinter}` : 'none';
  console.warn('[print] kitchen attempt', { printer: _printer, bytes: content?.length || 0, ...(printMeta || {}) });

  if (usbPrinter) {
    const res = await printServiceFetch(`${baseUrl}/print`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        printerName: usbPrinter,
        data: content,
        logoUrl,
        type: 'TEXT',
        receiptKind: 'kitchen',
        ...printCodePagePayload(),
        ...imagePayload,
      }),
    });
    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({}));
      const msg = errorBody.message || errorBody.error || 'Kitchen USB print failed';
      console.error('[print] kitchen USB FAILED', { printer: _printer, status: res.status, msg });
      throw new Error(msg);
    }
    console.warn('[print] kitchen USB success', { printer: _printer });
    return;
  }

  if (ipPrinter) {
    const res = await printServiceFetch(`${baseUrl}/print-ip`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ip: ipPrinter,
        port: 9100,
        data: content,
        logoUrl: logoUrl || undefined,
        receiptKind: 'kitchen',
        ...printCodePagePayload(),
        ...imagePayload,
      }),
    });
    if (!res.ok) {
      const errorBody = await res.json().catch(() => ({}));
      const msg = errorBody.message || errorBody.error || 'Kitchen IP print failed';
      console.error('[print] kitchen IP FAILED', { printer: _printer, status: res.status, msg });
      throw new Error(msg);
    }
    console.warn('[print] kitchen IP success', { printer: _printer });
    return;
  }

  console.error('[print] no kitchen printer configured');
  throw new Error('No kitchen printer configured (USB or IP in POS settings)');
};

/** Public kitchen-print entry — failed tickets are queued and retried. */
export const sendToKitchenPrinter = async (content, logoUrl = null, printMeta = null) => {
  try {
    return await sendToKitchenPrinterOnce(content, logoUrl, printMeta);
  } catch (err) {
    const isConfigError = String(err?.message || '').includes('printer configured');
    if (!printMeta?.__isRetry && !isConfigError) {
      enqueuePrintRetry('kitchen', { content, logoUrl, printMeta }, err);
    }
    // Alert staff immediately — but ONLY when the kitchen printer genuinely
    // failed to print. Two cases are deliberately silent:
    //   • retries (first attempt only, so a 5-minute outage doesn't stack 15
    //     popups), and
    //   • no kitchen printer configured on this device — nothing was even
    //     attempted, so a red "kitchen printer not working" alarm on every
    //     order is wrong and trains staff to dismiss it. That is a setup
    //     issue: it still reaches the cloud feed via the console.error in
    //     the sender, and the counter-print path has its own alert.
    if (!printMeta?.__isRetry && !isConfigError) {
      notifyKitchenPrintFailure({
        stage: 'failed',
        errMsg: String(err?.message || err),
        orderNo: printMeta?.orderNo ?? null,
        table: printMeta?.table ?? null,
        willRetry: true,
      });
    }
    throw err;
  }
};

async function printKitchenPlanSlice({
  merged,
  table,
  t,
  logoUrl,
  language,
  timezone,
  enableCounterItems,
  plan,
}) {
  const hasKitchenPrinter = !!(getSavedKitchenUsb() || getSavedKitchenIp());
  const hasCounterPrinter = !!(getSavedCounterUsb() || getSavedCounterIp());
  const sliceItems = plan.items || [];
  if (!sliceItems.length) return [];

  const kitchenOpts = {
    itemTypeFilter: plan.itemTypeFilter,
    itemsOverride: sliceItems,
  };

  // Order identity on every print log line — lets cloud-log forensics tie a
  // physical ticket to its order without timestamp guesswork.
  const printMeta = {
    orderId: merged?._id || merged?.id || null,
    orderNo: merged?.orderNumber ?? merged?.order_number ?? null,
    table: table?.number ?? merged?.tableNumber ?? merged?.table_number ?? null,
    items: sliceItems.length,
  };

  if (!enableCounterItems) {
    if (hasKitchenPrinter) {
      const kitchenText = buildKitchenReceiptText(
        merged,
        table,
        t,
        language,
        timezone,
        null,
        kitchenOpts
      );
      await sendToKitchenPrinter(kitchenText, logoUrl, printMeta);
    }
    return sliceItems;
  }

  const kitchenOnlyItems = sliceItems.filter((i) => !isCounterItem(i));
  const counterOnlyItems = sliceItems.filter((i) => isCounterItem(i));

  // Kitchen and counter are different printers — print both in parallel.
  const sends = [];
  if (kitchenOnlyItems.length > 0 && hasKitchenPrinter) {
    const kitchenText = buildKitchenReceiptText(
      merged,
      table,
      t,
      language,
      timezone,
      null,
      { ...kitchenOpts, itemsOverride: kitchenOnlyItems }
    );
    sends.push(sendToKitchenPrinter(kitchenText, logoUrl, printMeta));
  }

  if (counterOnlyItems.length > 0 && hasCounterPrinter) {
    const counterText = buildKitchenReceiptText(
      merged,
      table,
      t,
      language,
      timezone,
      t ? t('pos.counterOrder') : 'COUNTER ORDER',
      { ...kitchenOpts, itemsOverride: counterOnlyItems }
    );
    sends.push(sendToCounterPrinter(counterText, null, null, printMeta));
  } else if (counterOnlyItems.length > 0 && !hasCounterPrinter) {
    console.warn(
      '[print] Order has counter items but no counter printer is set in POS Settings (USB/IP).',
      counterOnlyItems.map((i) => i.name).join(', ')
    );
  }

  if (sends.length) await Promise.all(sends);

  return sliceItems;
}

/**
 * @returns {Promise<object|null>} order with kitchenPrinted marks applied on printed lines
 */
export const printKitchenReceiptForOrder = async (
  order,
  table,
  t,
  logoUrl = null,
  language = null,
  localSnapshot = null,
  timezone = null,
  enableCounterItems = true,
  branchId = null,
  kitchenPrintOptions = null
) => {
  const hasKitchenPrinter = !!(getSavedKitchenUsb() || getSavedKitchenIp());
  const hasCounterPrinter = !!(getSavedCounterUsb() || getSavedCounterIp());

  if (!hasKitchenPrinter && !hasCounterPrinter) return null;

  // Print in the configured receipt language (independent of app language).
  ({ t, language } = resolveReceiptLocale(t, language));

  let merged = mergeOrderForKitchenReceipt(order, localSnapshot);
  if (enableCounterItems) {
    if (branchId) {
      const counterMap = await getCounterItemMapForBranch(branchId);
      merged = enrichOrderItemsWithCounterFlags(merged, counterMap);
    } else {
      merged = enrichOrderItemsWithCounterFlags(merged, new Map());
    }
  }

  // Items the cashier tapped "no print" on in the cart are kept on the order
  // (and on the bill) but excluded from the kitchen/counter ticket.
  const printableItems = (merged.items || []).filter((it) => !it?.noPrint && !it?._noPrint);
  const plans = getKitchenPrintPlans(printableItems, kitchenPrintOptions);
  if (!plans.length) return null;

  let printedAll = [];
  for (const plan of plans) {
    const printed = await printKitchenPlanSlice({
      merged,
      table,
      t,
      logoUrl,
      language,
      timezone,
      enableCounterItems,
      plan,
    });
    printedAll = printedAll.concat(printed || []);
  }

  return printedAll.length ? applyKitchenPrintedMarks(merged, printedAll) : null;
};

/**
 * Cancellation ticket: when an order (or a set of items) is deleted, print a
 * kitchen ticket listing ALL the cancelled items under a "CANCELLED" heading so
 * the kitchen knows to stop. Works for any order type (dine-in / takeaway /
 * solo takeaway). Counter items go to the counter printer when configured.
 * @param {Array} itemsOverride  Optional subset of items to cancel (tab-scoped delete).
 */
export const printCancelledKitchenReceiptForOrder = async (
  order,
  table,
  t,
  language = null,
  timezone = null,
  branchId = null,
  enableCounterItems = true,
  itemsOverride = null
) => {
  const hasKitchenPrinter = !!(getSavedKitchenUsb() || getSavedKitchenIp());
  const hasCounterPrinter = !!(getSavedCounterUsb() || getSavedCounterIp());
  if (!hasKitchenPrinter && !hasCounterPrinter) return null;

  // Print in the configured receipt language (independent of app language).
  ({ t, language } = resolveReceiptLocale(t, language));

  let merged = order || {};
  if (enableCounterItems && branchId) {
    const counterMap = await getCounterItemMapForBranch(branchId);
    merged = enrichOrderItemsWithCounterFlags(merged, counterMap);
  }
  const allItems = (itemsOverride && itemsOverride.length ? itemsOverride : merged.items) || [];
  if (!allItems.length) return null;

  const kitchenItems = enableCounterItems ? allItems.filter((i) => !isCounterItem(i)) : allItems;
  const counterItems = enableCounterItems ? allItems.filter((i) => isCounterItem(i)) : [];

  // Build the cancel ticket via the modification ticket with all items as
  // "removed" → each line shows "- Nx Item", under a big centered CANCELLED.
  const buildText = (items) =>
    generateModificationKitchenTicket(
      merged,
      table,
      { added: [], removed: items, quantityChanged: [] },
      t,
      language,
      timezone,
      { headingMode: 'cancelled' }
    );

  const cancelMeta = {
    orderId: order?._id || order?.id || null,
    orderNo: order?.orderNumber ?? order?.order_number ?? null,
    table: table?.number ?? order?.tableNumber ?? order?.table_number ?? null,
    reason: 'cancelled',
  };
  const sends = [];
  if (kitchenItems.length && hasKitchenPrinter) {
    sends.push(sendToKitchenPrinter(buildText(kitchenItems), null, cancelMeta));
  }
  if (counterItems.length) {
    if (hasCounterPrinter) {
      // Separate CANCELLED receipt for counter items (its own printer).
      sends.push(sendToCounterPrinter(buildText(counterItems), null, null, cancelMeta));
    } else if (hasKitchenPrinter) {
      // No counter printer — fold counter items into the kitchen cancel ticket.
      sends.push(sendToKitchenPrinter(buildText(counterItems), null, cancelMeta));
    }
  }
  // When counter-item routing is off, kitchenItems already = all items above.

  if (sends.length) await Promise.allSettled(sends);
  return true;
};

export function getModificationKitchenPrintPlans(items = [], changes, kitchenPrintOptions = null) {
  const forced = kitchenPrintOptions?.itemTypeFilter;
  const types = new Set();
  for (const list of [changes?.added, changes?.removed, changes?.quantityChanged]) {
    for (const item of list || []) {
      types.add(getOrderItemType(item));
    }
  }
  if (!types.size) return [];

  if (forced === ITEM_TYPE_TAKEAWAY || forced === ITEM_TYPE_DINE_IN) {
    return types.has(forced) ? [{ itemTypeFilter: forced }] : [];
  }

  if (orderHasMixedItemTypes(items)) {
    return [...types].map((itemTypeFilter) => ({ itemTypeFilter }));
  }

  return [{ itemTypeFilter: null }];
}

/** Modification tickets: separate takeaway vs dine-in; counter items → counter printer when enabled. */
export const printModificationKitchenForOrder = async (
  order,
  table,
  previousItems,
  t,
  language = null,
  timezone = null,
  kitchenPrintOptions = null,
  enableCounterItems = true,
  branchId = null
) => {
  const hasKitchenPrinter = !!(getSavedKitchenUsb() || getSavedKitchenIp());
  const hasCounterPrinter = !!(getSavedCounterUsb() || getSavedCounterIp());
  if (!hasKitchenPrinter && !hasCounterPrinter) return null;

  // Print in the configured receipt language (independent of app language).
  ({ t, language } = resolveReceiptLocale(t, language));

  let orderForPrint = order;
  let prevItems = previousItems || [];
  if (enableCounterItems && branchId) {
    const counterMap = await getCounterItemMapForBranch(branchId);
    orderForPrint = enrichOrderItemsWithCounterFlags(order, counterMap);
    prevItems = enrichOrderItemsWithCounterFlags({ items: prevItems }, counterMap).items || [];
  }

  // Diff the FULL current items (including no-print ones) so a no-print item is
  // never mistaken for a removal → no false CANCELLED ticket. Then drop the
  // no-print items from the PRINTED change lines only.
  const allCurrentItems = orderForPrint.items || [];
  const changes = filterModificationChangesNoPrint(
    diffOrderItemChanges(prevItems, allCurrentItems)
  );
  if (modificationChangesEmpty(changes)) return null;

  const plans = getModificationKitchenPrintPlans(allCurrentItems, changes, kitchenPrintOptions);
  let printedAny = false;

  for (const plan of plans) {
    const byType = filterModificationChangesByItemType(changes, plan.itemTypeFilter);
    if (modificationChangesEmpty(byType)) continue;

    if (!enableCounterItems) {
      if (hasKitchenPrinter) {
        printedAny =
          (await printModificationChangeSlice({
            order: orderForPrint,
            table,
            changes: byType,
            t,
            language,
            timezone,
            itemTypeFilter: plan.itemTypeFilter,
            sendFn: sendToKitchenPrinter,
          })) || printedAny;
      }
      continue;
    }

    const kitchenChanges = filterModificationChangesByCounter(byType, false);
    const counterChanges = filterModificationChangesByCounter(byType, true);

    // Kitchen and counter are different printers — print both in parallel.
    const sliceSends = [];
    if (hasKitchenPrinter && !modificationChangesEmpty(kitchenChanges)) {
      sliceSends.push(
        printModificationChangeSlice({
          order: orderForPrint,
          table,
          changes: kitchenChanges,
          t,
          language,
          timezone,
          itemTypeFilter: plan.itemTypeFilter,
          sendFn: sendToKitchenPrinter,
        })
      );
    }

    if (hasCounterPrinter && !modificationChangesEmpty(counterChanges)) {
      sliceSends.push(
        printModificationChangeSlice({
          order: orderForPrint,
          table,
          changes: counterChanges,
          t,
          language,
          timezone,
          itemTypeFilter: plan.itemTypeFilter,
          // No title prefix — the modification slip should read just "Running"
          // (don't print the COUNTER ORDER section title).
          sendFn: (ticket) => sendToCounterPrinter(ticket, null, null),
        })
      );
    } else if (!modificationChangesEmpty(counterChanges) && !hasCounterPrinter) {
      console.warn(
        '[print] Modification has counter items but no counter printer is configured.',
        counterChanges.added?.map((i) => i.name).join(', ')
      );
    }

    if (sliceSends.length) {
      const results = await Promise.all(sliceSends);
      printedAny = results.some(Boolean) || printedAny;
    }
  }

  return printedAny ? orderForPrint : null;
};
